import { Injectable } from '@angular/core';
import {
  HasPermissionGuardActions,
  IsAlreadyLoggedInGuardActions,
  IsLoggedInGuardActions,
  JwtTokenActions,
  LoginFormActions,
  UserEffectActions,
} from '@elio/emanager-front/auth/action';
import { RouterSelectors } from '@elio/emanager-front/shared/data-access/router';

import { HqProfessionalPushActions } from '@elio/emanager-front/professional/headquarter/action';
import { ProfessionalPushActions } from '@elio/emanager-front/professional/offer/action';
import { MessagingSubscriptionActions } from '@elio/emanager-front/push-messaging/action';
import { ElioTypedRouter } from '@elio/emanager-front/shared/tools/router';
import { ElioError } from '@elio/shared/models';
import { catchElioError } from '@elio/shared/rxjs-utils';
import {
  localStorageGet,
  localStorageRemove,
  localStorageSave,
  sessionStorageGet,
  sessionStorageRemove,
  sessionStorageSave,
} from '@elio/shared/utils';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, map, mergeMap, of, tap } from 'rxjs';
import { UserSelectors } from './user.selectors';
import { UserService } from './user.service';
import { UserUtils } from './user.utils';

@Injectable()
export class UserEffects {
  loadUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        HasPermissionGuardActions.loadUser,
        IsLoggedInGuardActions.loadUser,
        IsAlreadyLoggedInGuardActions.loadUser
      ),
      concatLatestFrom(() => this.store.select(UserSelectors.isLoggedIn)),
      mergeMap(([action, isLoggedIn]) => {
        if (isLoggedIn) {
          return of(UserEffectActions.userIsAlreadyLoaded());
        }

        const jwtAccessToken = localStorageGet<string>('jwtAccessToken') ?? sessionStorageGet<string>('jwtAccessToken');
        if (jwtAccessToken) {
          return this.userService.getUser(jwtAccessToken).pipe(
            map((user) => UserEffectActions.loadUserSuccess({ user })),
            catchElioError((error) => of(UserEffectActions.loadUserFailure({ error })))
          );
        } else {
          const jwtError = new ElioError('NO_JWT_ACCESS_TOKEN', 'No user jwt access token found');
          return of(
            // avoid infinite loop of redirectToLogin$ in case it fails to load user for IsAlreadyLoggedInGuard on /login
            action.type !== IsAlreadyLoggedInGuardActions.loadUser.type
              ? UserEffectActions.loadUserFailure({ error: jwtError })
              : UserEffectActions.loadUserFailureNoRedirection({ error: jwtError })
          );
        }
      })
    );
  });

  saveJwtTokenInStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginFormActions.loginSuccess, JwtTokenActions.refreshJwtTokenSuccess),
        concatLatestFrom(() => this.store.select(UserSelectors.isRememberMe)),
        tap(([action, rememberMe]) => {
          if (rememberMe) {
            localStorageSave<string>('jwtAccessToken', action.jwtAccessToken);
          } else {
            sessionStorageSave<string>('jwtAccessToken', action.jwtAccessToken);
          }
        })
      );
    },
    { dispatch: false }
  );

  redirectToLogin$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          JwtTokenActions.refreshJwtTokenFailure,
          MessagingSubscriptionActions.successAndLogout,
          MessagingSubscriptionActions.failureAndLogout,
          UserEffectActions.loadUserFailure
        ),
        mergeMap(() => this.userService.logout()),
        tap(() => {
          this.router.navigate(['login'], { state: { skipGuard: true } });
        })
      );
    },
    { dispatch: false }
  );

  clearStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          JwtTokenActions.refreshJwtTokenFailure,
          MessagingSubscriptionActions.successAndLogout,
          MessagingSubscriptionActions.failureAndLogout,
          UserEffectActions.loadUserFailure
        ),
        tap(() => {
          localStorageRemove('jwtAccessToken');
          sessionStorageRemove('jwtAccessToken');
        })
      );
    },
    { dispatch: false }
  );

  userRedirection$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          LoginFormActions.loginSuccess,
          ProfessionalPushActions.leaveOneOffer,
          HqProfessionalPushActions.leaveOneHeadquarter,
          UserEffectActions.userIsNavigatingOnUnauthorizedOffer,
          UserEffectActions.userIsNavigatingOnUnauthorizedHeadquarter
        ),
        tap((action) => this.router.navigate(UserUtils.userRedirection(action.user)))
      );
    },
    { dispatch: false }
  );

  checkOffersAfterJwtTokenRefresh$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(JwtTokenActions.refreshJwtTokenSuccess),
      concatLatestFrom(() => [
        this.store.select(UserSelectors.getUser),
        this.store.select(RouterSelectors.getOfferIdFromRouteParam),
        this.store.select(UserSelectors.getCurrentHeadquarter),
      ]),
      mergeMap(([action, currentUser, currentOfferId, currentHq]) => {
        if (currentUser && !currentUser.isSuperAdmin) {
          const userStoredOffers = currentUser.offerIds;
          const userTokenOffers = action.jwtAccessTokenPayload.availableOffers;
          const userStoredHeadquarters: number[] = currentUser.headquarters.map((obj) => obj.id);
          const userTokenHeadquarters = action.jwtAccessTokenPayload.availableHeadquarters;

          if (userTokenOffers.length === 0 && userTokenHeadquarters.length === 0) {
            return of(UserEffectActions.userHasNoOffersNorHqLeft());
          }
          const unauthorizedOffers = userStoredOffers.filter((num) => !userTokenOffers.includes(num));
          if (unauthorizedOffers.length > 0) {
            if (currentOfferId && unauthorizedOffers.includes(currentOfferId)) {
              return of(
                UserEffectActions.userIsNavigatingOnUnauthorizedOffer({ user: currentUser, unauthorizedOffers })
              );
            } else {
              return of(UserEffectActions.userHasUnauthorizedOffers({ user: currentUser, unauthorizedOffers }));
            }
          }
          const unauthorizedHq = userStoredHeadquarters.filter((num) => !userTokenHeadquarters.includes(num));
          if (unauthorizedHq.length > 0) {
            if (currentHq && unauthorizedHq.includes(currentHq.id)) {
              return of(UserEffectActions.userIsNavigatingOnUnauthorizedHeadquarter({ user: currentUser }));
            }
          }
        }
        return EMPTY;
      })
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private userService: UserService,
    private router: ElioTypedRouter
  ) {}
}
